import clsx from "clsx"
import { ReactElement, useRef, useLayoutEffect, useState } from "react"
import { MessageAttachmentCard } from "./MessageAttachmentCard"

// Notes:
// =========================================================================================================
// - Need to return to Forex/Bonds tab to figure out licensing
// - for some reason groq believes whatever price the user puts in otherwise the price seems to be wrong
// - but now it just says price pending and lets the user read the price chart instead
// =========================================================================================================

// Interface for the JSON response from the simplified backend
interface WidgetData {
    type: string;
    symbol: string | null;
    config: Record<string, any>;
}

interface BackendResponse {
    success: boolean;
    response_text: string;
    widget: WidgetData | null;
    error: string | null;
}

// Script-executing HTML component (for regular content without widgets)
interface ScriptExecutingHtmlProps {
    html: string;
    className?: string;
}

const ScriptExecutingHtml: React.FC<ScriptExecutingHtmlProps> = ({ html, className }) => {
    const elRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!elRef.current || !html) return;

        console.log('📝 Regular content - executing scripts directly in DOM');

        // Create a document range for proper context
        const range = document.createRange();
        range.selectNode(elRef.current);
        
        // Create fragment that will execute scripts
        const documentFragment = range.createContextualFragment(html);
        
        // Clear and inject the markup, triggering script execution
        elRef.current.innerHTML = '';
        elRef.current.appendChild(documentFragment);
    }, [html]);

    return <div ref={elRef} className={className} />;
};

// TradingView widget renderer (DOM-stable, calls your helper functions)
interface TradingViewWidgetRendererProps {
    widget: WidgetData;
  }
  
  const TradingViewWidgetRenderer: React.FC<TradingViewWidgetRendererProps> = ({ widget }) => {
    const containerRef = useRef<HTMLDivElement>(null);
  
    useLayoutEffect(() => {
      if (!containerRef.current || !widget) return;
  
      const container = containerRef.current;
      const containerId = `tv-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      container.id = containerId;
  
      // Keep the DOM stable: clear once, never swap this node again.
      container.innerHTML = '';
  
      // No-op setters to avoid React state flips while the external script mutates DOM
      const noopSetLoading = (_: boolean) => {};
      const noopSetError = (_: string | null) => {};
  
      const { type, symbol, config } = widget;
  
      try {
        // Use your proven helper functions
        if (type === 'financials') {
          _createFinancialsWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'chart') {
          _createChartWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'price') {
          _createPriceWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'news') {
          _createNewsWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'screener') {
          _createScreenerWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'market_overview') {
          _createMarketOverviewWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'market_heatmap') {
          _createMarketHeatmapWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'trending_stocks') {
          _createTrendingStocksWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'etf_heatmap') {
          _createETFHeatmapWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'technical_analysis') {
          _createTechnicalAnalysisWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'company_profile') {
          _createCompanyProfileWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'symbol_info') {
          _createSymbolInfoWidget(containerId, symbol, config, noopSetLoading, noopSetError);
        } else if (type === 'top_stories') {
          _createTopStoriesWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'forex_cross_rates') {
          _createForexCrossRatesWidget(containerId, config, noopSetLoading, noopSetError);
        } else if (type === 'economic_calendar') {
          _createEconomicCalendarWidget(containerId, config, noopSetLoading, noopSetError);
        } else {
          console.warn(`⚠️ Unknown widget type: ${type}`);
        }
  
        // Debug hook: confirms when TradingView attaches its <iframe>
        const observer = new MutationObserver((mutations) => {
          for (const m of mutations) {
            for (const node of Array.from(m.addedNodes)) {
              if ((node as HTMLElement).tagName === 'IFRAME') {
                console.log(`🎉 TradingView iframe attached for ${type}${symbol ? ` (${symbol})` : ''}`);
                observer.disconnect();
                return;
              }
            }
          }
        });
        observer.observe(container, { childList: true });
  
      } catch (err) {
        console.error('💥 Error creating TradingView widget:', err);
        // Render a simple inline error (no React state updates)
        container.innerHTML = `<div style="padding:12px;border:1px solid rgba(255,0,0,.25);border-radius:8px;color:#c00;">
          ⚠️ ${err instanceof Error ? err.message : 'Unknown error creating widget'}
        </div>`;
      }
    }, [widget]);
  
    // Single stable host node — no loading/error wrappers that get swapped out
    return (
      <div
        ref={containerRef}
        className="tradingview-widget-container"
        style={{
          width: '100%',
          minHeight: widget.config.height || '400px',
          background: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      />
    );
  };  
  

// Helper function to get display name for widget type
function _getWidgetDisplayName(type: string): string {
    const displayNames: Record<string, string> = {
        'financials': 'Financial Data',
        'chart': 'Chart',
        'price': 'Price & Overview',
        'news': 'Latest News & Events',
        'screener': 'Stock Screener',
        'market_overview': 'Market Overview',
        'market_heatmap': 'Market Heatmap',
        'trending_stocks': 'Trending Stocks',
        'etf_heatmap': 'ETF Heatmap',
        // NEW WIDGETS ADDED BELOW
        'technical_analysis': 'Technical Analysis',
        'company_profile': 'Company Profile',
        'symbol_info': 'Symbol Overview',
        'top_stories': 'Top Market Stories',
        'forex_cross_rates': 'Forex Cross Rates',
        'economic_calendar': 'Economic Calendar'
    };
    return displayNames[type] || 'TradingView Widget';
}

// Widget creation functions (exactly like your Flask app)
function _createFinancialsWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating financials widget using Flask app approach');
    
    const initFinancials = () => {
        const container = document.getElementById(containerId);
        if (!container) {
            console.error('Container not found for financials widget:', containerId);
            setError('Container not found');
            return;
        }

        // Prevent duplicate widgets
        if (container.querySelector('script[src*="embed-widget-financials"]')) {
            console.log('⚠️ Financials widget already exists, skipping creation');
            return;
        }

        if (typeof (window as any).TradingView !== 'undefined') {
            try {
                // Clear container completely
                container.innerHTML = '';
                
                // Add a small delay to ensure DOM stability
                setTimeout(() => {
                    const script = document.createElement('script');
                    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-financials.js';
                    script.async = true;
                    script.innerHTML = JSON.stringify({
                        "isTransparent": config.isTransparent || false,
                        "largeChartUrl": config.largeChartUrl || "",
                        "displayMode": config.displayMode || "regular",
                        "width": config.width || "100%",
                        "height": config.height || "830",
                        "colorTheme": config.colorTheme || "light",
                        "symbol": symbol || "NASDAQ:AAPL",
                        "locale": config.locale || "en"
                    });
                    
                    script.onload = () => {
                        console.log('✅ Financials widget script loaded');
                        setIsLoading(false);
                    };
                    
                    script.onerror = (error) => {
                        console.error('💥 Failed to load financials widget script:', error);
                        setError('Failed to load financial data widget');
                        setIsLoading(false);
                    };
                    
                    container.appendChild(script);
                    console.log('📝 Financials script appended to container');
                }, 100);
                
            } catch (e) {
                console.error('Error creating financials widget:', e);
                container.innerHTML = '<p>Error loading financial data</p>';
                setError('Error creating financials widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initFinancials, 100);
        }
    };
    
    // Only initialize once DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFinancials);
    } else {
        // Add delay to ensure React rendering is complete
        setTimeout(initFinancials, 200);
    }
}

function _createChartWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating chart widget using Flask app approach');
    
    const initChart = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined' && (window as any).TradingView.widget) {
            try {
                new (window as any).TradingView.widget({
                    "symbol": symbol || "NASDAQ:AAPL",
                    "container_id": containerId,
                    "width": config.width || "100%",
                    "height": config.height || "400",
                    "interval": config.interval || "D",
                    "timezone": config.timezone || "Etc/UTC",
                    "theme": config.theme || "light",
                    "style": config.style || "1",
                    "locale": config.locale || "en",
                    "toolbar_bg": config.toolbar_bg || "#f1f3f6",
                    "enable_publishing": config.enable_publishing || false,
                    "allow_symbol_change": config.allow_symbol_change || true,
                    "hide_side_toolbar": config.hide_side_toolbar || false,
                    "hide_top_toolbar": config.hide_top_toolbar || false,
                    "hide_legend": config.hide_legend || false,
                    "save_image": config.save_image || false,
                    "withdateranges": config.withdateranges || true
                });
                
                console.log('✅ Chart widget created');
                setIsLoading(false);
                
            } catch (e) {
                console.error('Error creating chart widget:', e);
                container.innerHTML = '<p>Error loading chart</p>';
                setError('Error creating chart widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initChart, 100);
        }
    };
    
    if (typeof (window as any).TradingView === 'undefined') {
        const tvScript = document.createElement('script');
        tvScript.src = 'https://s3.tradingview.com/tv.js';
        tvScript.async = true;
        tvScript.onload = () => {
            console.log('📚 TradingView library loaded for chart');
            setTimeout(initChart, 100);
        };
        tvScript.onerror = () => {
            console.error('💥 Failed to load TradingView library');
            setError('Failed to load TradingView library');
            setIsLoading(false);
        };
        document.head.appendChild(tvScript);
    } else {
        initChart();
    }
}

function _createPriceWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating price widget using Flask app approach');
    
    const initPrice = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined' && (window as any).TradingView.widget) {
            try {
                new (window as any).TradingView.widget({
                    "container_id": containerId,
                    "width": config.width || "100%",
                    "height": config.height || "400",
                    "symbol": symbol || "NASDAQ:AAPL",
                    "interval": config.interval || "1D",
                    "timezone": config.timezone || "Etc/UTC",
                    "theme": config.theme || "light",
                    "style": config.style || "3",
                    "locale": config.locale || "en",
                    "toolbar_bg": config.toolbar_bg || "#f1f3f6",
                    "enable_publishing": config.enable_publishing || false,
                    "hide_side_toolbar": config.hide_side_toolbar || true,
                    "hide_top_toolbar": config.hide_top_toolbar || true,
                    "hide_legend": config.hide_legend || true,
                    "save_image": config.save_image || false,
                    "autosize": config.autosize || true,
                    "show_popup_button": config.show_popup_button || false
                });
                
                console.log('✅ Price widget created');
                setIsLoading(false);
                
            } catch (e) {
                console.error('Error creating price widget:', e);
                container.innerHTML = '<p>Error loading price data</p>';
                setError('Error creating price widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initPrice, 100);
        }
    };
    
    if (typeof (window as any).TradingView === 'undefined') {
        const tvScript = document.createElement('script');
        tvScript.src = 'https://s3.tradingview.com/tv.js';
        tvScript.async = true;
        tvScript.onload = () => {
            console.log('📚 TradingView library loaded for price');
            setTimeout(initPrice, 100);
        };
        tvScript.onerror = () => {
            console.error('💥 Failed to load TradingView library');
            setError('Failed to load TradingView library');
            setIsLoading(false);
        };
        document.head.appendChild(tvScript);
    } else {
        initPrice();
    }
}

function _createNewsWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating news widget using Flask app approach');
    
    const initNews = () => {
        const container = document.getElementById(containerId);
        if (!container) {
            console.error('Container not found for news widget:', containerId);
            setError('Container not found');
            return;
        }

        // Prevent duplicate widgets
        if (container.querySelector('script[src*="embed-widget-timeline"]')) {
            console.log('⚠️ News widget already exists, skipping creation');
            return;
        }

        if (typeof (window as any).TradingView !== 'undefined') {
            try {
                // Clear container completely
                container.innerHTML = '';
                
                // Add a small delay to ensure DOM stability
                setTimeout(() => {
                    const script = document.createElement('script');
                    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-timeline.js';
                    script.async = true;
                    script.innerHTML = JSON.stringify({
                        "feedMode": config.feedMode || "symbol",
                        "symbol": symbol || "NASDAQ:AAPL",
                        "colorTheme": config.colorTheme || "light",
                        "isTransparent": config.isTransparent || false,
                        "displayMode": config.displayMode || "regular",
                        "width": config.width || "100%",
                        "height": config.height || "500",
                        "locale": config.locale || "en"
                    });
                    
                    script.onload = () => {
                        console.log('✅ News widget script loaded');
                        setIsLoading(false);
                    };
                    
                    script.onerror = (error) => {
                        console.error('💥 Failed to load news widget script:', error);
                        setError('Failed to load news widget');
                        setIsLoading(false);
                    };
                    
                    container.appendChild(script);
                    console.log('📝 News script appended to container');
                }, 100);
                
            } catch (e) {
                console.error('Error creating news widget:', e);
                container.innerHTML = '<p>Error loading news</p>';
                setError('Error creating news widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initNews, 100);
        }
    };
    
    // Only initialize once DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initNews);
    } else {
        // Add delay to ensure React rendering is complete
        setTimeout(initNews, 200);
    }
}

function _createScreenerWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating screener widget using Flask app approach');
    
    const initScreener = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-screener.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "width": config.width || "100%",
                    "height": config.height || "500",
                    "defaultColumn": config.defaultColumn || "overview",
                    "defaultScreen": config.defaultScreen || "most_capitalized",
                    "market": config.market || "america",
                    "showToolbar": config.showToolbar || true,
                    "colorTheme": config.colorTheme || "light",
                    "locale": config.locale || "en"
                });
                
                script.onload = () => {
                    console.log('✅ Screener widget script loaded');
                    setIsLoading(false);
                };
                
                script.onerror = () => {
                    console.error('💥 Failed to load screener widget script');
                    setError('Failed to load screener widget');
                    setIsLoading(false);
                };
                
                container.appendChild(script);
                console.log('📝 Screener script appended to container');
                
            } catch (e) {
                console.error('Error creating screener widget:', e);
                container.innerHTML = '<p>Error loading stock screener</p>';
                setError('Error creating screener widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initScreener, 100);
        }
    };
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initScreener);
    } else {
        setTimeout(initScreener, 500);
    }
}

function _createMarketOverviewWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating market overview widget using Flask app approach');
    
    const initOverview = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js';
                script.async = true;
                
                // FIXED: Updated tabs to avoid CME permission errors
                const defaultTabs = [
                    {
                        "title": "Indices",
                        "symbols": [
                            {"s": "FOREXCOM:SPXUSD", "d": "S&P 500"},
                            {"s": "FOREXCOM:NSXUSD", "d": "US 100"},
                            {"s": "FOREXCOM:DJI", "d": "Dow 30"},
                            {"s": "INDEX:NKY", "d": "Nikkei 225"},
                            {"s": "INDEX:DEU40", "d": "DAX Index"},
                            {"s": "FOREXCOM:UKXGBP", "d": "UK 100"}
                        ],
                    },
                    {
                        "title": "Commodities",
                        "symbols": [
                            {"s": "FOREXCOM:XAUUSD", "d": "Gold"},
                            {"s": "FOREXCOM:XAGUSD", "d": "Silver"},
                            {"s": "FOREXCOM:UKOIL", "d": "Crude Oil"},
                            {"s": "FOREXCOM:NATURALGAS", "d": "Natural Gas"},
                            {"s": "FOREXCOM:CORN", "d": "Corn"},
                            {"s": "FOREXCOM:WHEAT", "d": "Wheat"}
                        ],
                    },
                    {
                        "title": "Forex",
                        "symbols": [
                            {"s": "FX:EURUSD", "d": "EUR to USD"},
                            {"s": "FX:GBPUSD", "d": "GBP to USD"},
                            {"s": "FX:USDJPY", "d": "USD to JPY"},
                            {"s": "FX:USDCHF", "d": "USD to CHF"},
                            {"s": "FX:AUDUSD", "d": "AUD to USD"},
                            {"s": "FX:USDCAD", "d": "USD to CAD"}
                        ],
                    },
                    {
                        "title": "Cryptocurrency",
                        "symbols": [
                            {"s": "COINBASE:BTCUSD", "d": "Bitcoin"},
                            {"s": "COINBASE:ETHUSD", "d": "Ethereum"},
                            {"s": "COINBASE:XRPUSD", "d": "XRP"},
                            {"s": "COINBASE:SOLUSD", "d": "Solana"},
                            {"s": "COINBASE:ADAUSD", "d": "Cardano"},
                            {"s": "COINBASE:DOGEUSD", "d": "Dogecoin"}
                        ],
                    },
                    //Forex/Bonds need to be relooked at under here
                ];
                
                const marketConfig = {
                    "colorTheme": config.colorTheme || "light",
                    "dateRange": config.dateRange || "1D",
                    "showChart": config.showChart || true,
                    "locale": config.locale || "en",
                    "width": config.width || "100%",
                    "height": config.height || "600",
                    "largeChartUrl": config.largeChartUrl || "",
                    "isTransparent": config.isTransparent || false,
                    "showSymbolLogo": config.showSymbolLogo || true,
                    "showFloatingTooltip": config.showFloatingTooltip || false,
                    "plotLineColorGrowing": config.plotLineColorGrowing || "rgba(41, 98, 255, 1)",
                    "plotLineColorFalling": config.plotLineColorFalling || "rgba(41, 98, 255, 1)",
                    "gridLineColor": config.gridLineColor || "rgba(240, 243, 250, 0)",
                    "scaleFontColor": config.scaleFontColor || "rgba(106, 109, 120, 1)",
                    "belowLineFillColorGrowing": config.belowLineFillColorGrowing || "rgba(41, 98, 255, 0.12)",
                    "belowLineFillColorFalling": config.belowLineFillColorFalling || "rgba(41, 98, 255, 0.12)",
                    "belowLineFillColorGrowingBottom": config.belowLineFillColorGrowingBottom || "rgba(41, 98, 255, 0)",
                    "belowLineFillColorFallingBottom": config.belowLineFillColorFallingBottom || "rgba(41, 98, 255, 0)",
                    "symbolActiveColor": config.symbolActiveColor || "rgba(41, 98, 255, 0.12)",
                    "tabs": config.tabs && config.tabs.length > 0 ? config.tabs : defaultTabs  // ← KEY FIX
                };
                
                script.innerHTML = JSON.stringify(marketConfig);
                
                script.onload = () => {
                    console.log('✅ Market overview widget script loaded');
                    setIsLoading(false);
                };
                
                script.onerror = () => {
                    console.error('💥 Failed to load market overview widget');
                    setError('Failed to load market overview widget');
                    setIsLoading(false);
                };
                
                container.appendChild(script);
                console.log('📝 Market overview script appended to container');
                
            } catch (e) {
                console.error('Error creating market overview widget:', e);
                container.innerHTML = '<p>Error loading market overview</p>';
                setError('Error creating market overview widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initOverview, 100);
        }
    };
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initOverview);
    } else {
        setTimeout(initOverview, 500);
    }
}

function _createMarketHeatmapWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating market heatmap widget using Flask app approach');
    
    const initHeatmap = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-stock-heatmap.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "exchanges": config.exchanges || [],
                    "dataSource": config.dataSource || "SPX500",
                    "grouping": config.grouping || "sector",
                    "blockSize": config.blockSize || "market_cap_basic",
                    "blockColor": config.blockColor || "change",
                    "locale": config.locale || "en",
                    "symbolUrl": config.symbolUrl || "",
                    "colorTheme": config.colorTheme || "light",
                    "hasTopBar": config.hasTopBar || false,
                    "isDataSetEnabled": config.isDataSetEnabled || false,
                    "isZoomEnabled": config.isZoomEnabled || true,
                    "hasSymbolTooltip": config.hasSymbolTooltip || true,
                    "width": config.width || "100%",
                    "height": config.height || "600"
                });
                
                script.onload = () => {
                    console.log('✅ Market heatmap widget script loaded');
                    setIsLoading(false);
                };
                
                script.onerror = () => {
                    console.error('💥 Failed to load market heatmap widget');
                    setError('Failed to load market heatmap widget');
                    setIsLoading(false);
                };
                
                container.appendChild(script);
                console.log('📝 Market heatmap script appended to container');
                
            } catch (e) {
                console.error('Error creating market heatmap widget:', e);
                container.innerHTML = '<p>Error loading market heatmap</p>';
                setError('Error creating market heatmap widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initHeatmap, 100);
        }
    };
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initHeatmap);
    } else {
        setTimeout(initHeatmap, 500);
    }
}

function _createTrendingStocksWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating trending stocks widget using Flask app approach');
    
    const initTrending = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-hotlists.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "colorTheme": config.colorTheme || "light",
                    "dateRange": config.dateRange || "1D",
                    "exchange": config.exchange || "US",
                    "showChart": config.showChart || true,
                    "locale": config.locale || "en",
                    "largeChartUrl": config.largeChartUrl || "",
                    "isTransparent": config.isTransparent || false,
                    "showSymbolLogo": config.showSymbolLogo || false,
                    "showFloatingTooltip": config.showFloatingTooltip || false,
                    "width": config.width || "100%",
                    "height": config.height || "600",
                    "plotLineColorGrowing": config.plotLineColorGrowing || "rgba(41, 98, 255, 1)",
                    "plotLineColorFalling": config.plotLineColorFalling || "rgba(41, 98, 255, 1)",
                    "gridLineColor": config.gridLineColor || "rgba(240, 243, 250, 0)",
                    "scaleFontColor": config.scaleFontColor || "rgba(106, 109, 120, 1)",
                    "belowLineFillColorGrowing": config.belowLineFillColorGrowing || "rgba(41, 98, 255, 0.12)",
                    "belowLineFillColorFalling": config.belowLineFillColorFalling || "rgba(41, 98, 255, 0.12)",
                    "belowLineFillColorGrowingBottom": config.belowLineFillColorGrowingBottom || "rgba(41, 98, 255, 0)",
                    "belowLineFillColorFallingBottom": config.belowLineFillColorFallingBottom || "rgba(41, 98, 255, 0)",
                    "symbolActiveColor": config.symbolActiveColor || "rgba(41, 98, 255, 0.12)"
                });
                
                script.onload = () => {
                    console.log('✅ Trending stocks widget script loaded');
                    setIsLoading(false);
                };
                
                script.onerror = () => {
                    console.error('💥 Failed to load trending stocks widget script');
                    setError('Failed to load trending stocks widget');
                    setIsLoading(false);
                };
                
                container.appendChild(script);
                console.log('📝 Trending stocks script appended to container');
                
            } catch (e) {
                console.error('Error creating trending stocks widget:', e);
                container.innerHTML = '<p>Error loading trending stocks</p>';
                setError('Error creating trending stocks widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initTrending, 100);
        }
    };
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initTrending);
    } else {
        setTimeout(initTrending, 500);
    }
}

function _createETFHeatmapWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating ETF heatmap widget using Flask app approach');
    
    const initETFHeatmap = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-etf-heatmap.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "dataSource": config.dataSource || "AllUSEtf",
                    "blockSize": config.blockSize || "aum",
                    "blockColor": config.blockColor || "Perf.1D",
                    "grouping": config.grouping || "asset_class",
                    "locale": config.locale || "en",
                    "symbolUrl": config.symbolUrl || "",
                    "colorTheme": config.colorTheme || "light",
                    "hasTopBar": config.hasTopBar || false,
                    "isDataSetEnabled": config.isDataSetEnabled || false,
                    "isZoomEnabled": config.isZoomEnabled || true,
                    "hasSymbolTooltip": config.hasSymbolTooltip || true,
                    "width": config.width || "100%",
                    "height": config.height || "600"
                });
                
                script.onload = () => {
                    console.log('✅ ETF heatmap widget script loaded');
                    setIsLoading(false);
                };
                
                script.onerror = () => {
                    console.error('💥 Failed to load ETF heatmap widget script');
                    setError('Failed to load ETF heatmap widget');
                    setIsLoading(false);
                };
                
                container.appendChild(script);
                console.log('📝 ETF heatmap script appended to container');
                
            } catch (e) {
                console.error('Error creating ETF heatmap widget:', e);
                container.innerHTML = '<p>Error loading ETF heatmap</p>';
                setError('Error creating ETF heatmap widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initETFHeatmap, 100);
        }
    };
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initETFHeatmap);
    } else {
        setTimeout(initETFHeatmap, 500);
    }
}

function _createTechnicalAnalysisWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating technical analysis widget');
    
    const initWidget = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "interval": config.interval || "1m",
                    "width": config.width || "100%",
                    "isTransparent": config.isTransparent || false,
                    "height": config.height || 450,
                    "symbol": symbol || "NASDAQ:AAPL",
                    "showIntervalTabs": config.showIntervalTabs || true,
                    "displayMode": config.displayMode || "single",
                    "locale": config.locale || "en",
                    "colorTheme": config.colorTheme || "light"
                } );
                
                script.onload = () => setIsLoading(false);
                script.onerror = () => { setError('Failed to load technical analysis widget'); setIsLoading(false); };
                
                container.appendChild(script);
            } catch (e) {
                container.innerHTML = '<p>Error loading technical analysis</p>';
                setError('Error creating technical analysis widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initWidget, 100);
        }
    };
    
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWidget);
    else setTimeout(initWidget, 100);
}

function _createCompanyProfileWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating company profile widget');
    
    const initWidget = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-profile.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "width": config.width || "100%",
                    "height": config.height || 550,
                    "colorTheme": config.colorTheme || "light",
                    "isTransparent": config.isTransparent || false,
                    "symbol": symbol || "NASDAQ:AAPL",
                    "locale": config.locale || "en"
                } );
                
                script.onload = () => setIsLoading(false);
                script.onerror = () => { setError('Failed to load company profile widget'); setIsLoading(false); };
                
                container.appendChild(script);
            } catch (e) {
                container.innerHTML = '<p>Error loading company profile</p>';
                setError('Error creating company profile widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initWidget, 100);
        }
    };
    
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWidget);
    else setTimeout(initWidget, 100);
}

function _createSymbolInfoWidget(
    containerId: string, 
    symbol: string | null, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating symbol info widget');
    
    const initWidget = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-info.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "symbol": symbol || "NASDAQ:AAPL",
                    "width": config.width || "100%",
                    "locale": config.locale || "en",
                    "colorTheme": config.colorTheme || "light",
                    "isTransparent": config.isTransparent || false
                } );
                
                script.onload = () => setIsLoading(false);
                script.onerror = () => { setError('Failed to load symbol info widget'); setIsLoading(false); };
                
                container.appendChild(script);
            } catch (e) {
                container.innerHTML = '<p>Error loading symbol info</p>';
                setError('Error creating symbol info widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initWidget, 100);
        }
    };
    
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWidget);
    else setTimeout(initWidget, 100);
}

function _createTopStoriesWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating top stories widget');
    
    const initWidget = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                // Top Stories uses the same script as News, just different config
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-timeline.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "feedMode": config.feedMode || "all_symbols",
                    "colorTheme": config.colorTheme || "light",
                    "isTransparent": config.isTransparent || false,
                    "displayMode": config.displayMode || "regular",
                    "width": config.width || "100%",
                    "height": config.height || 550,
                    "locale": config.locale || "en"
                } );
                
                script.onload = () => setIsLoading(false);
                script.onerror = () => { setError('Failed to load top stories widget'); setIsLoading(false); };
                
                container.appendChild(script);
            } catch (e) {
                container.innerHTML = '<p>Error loading top stories</p>';
                setError('Error creating top stories widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initWidget, 100);
        }
    };
    
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWidget);
    else setTimeout(initWidget, 100);
}

function _createForexCrossRatesWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating forex cross rates widget');
    
    const initWidget = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-forex-cross-rates.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "width": config.width || "100%",
                    "height": config.height || 400,
                    "currencies": config.currencies || ["EUR", "USD", "JPY", "GBP", "CHF", "AUD", "CAD", "NZD", "CNY"],
                    "isTransparent": config.isTransparent || false,
                    "colorTheme": config.colorTheme || "light",
                    "locale": config.locale || "en",
                    "backgroundColor": config.backgroundColor || "#ffffff"
                } );
                
                script.onload = () => setIsLoading(false);
                script.onerror = () => { setError('Failed to load forex widget'); setIsLoading(false); };
                
                container.appendChild(script);
            } catch (e) {
                container.innerHTML = '<p>Error loading forex rates</p>';
                setError('Error creating forex widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initWidget, 100);
        }
    };
    
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWidget);
    else setTimeout(initWidget, 100);
}

function _createEconomicCalendarWidget(
    containerId: string, 
    config: Record<string, any>,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
): void {
    console.log('🚀 Creating economic calendar widget');
    
    const initWidget = () => {
        const container = document.getElementById(containerId);
        if (container && typeof (window as any).TradingView !== 'undefined') {
            try {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-events.js';
                script.async = true;
                script.innerHTML = JSON.stringify({
                    "width": config.width || "100%",
                    "height": config.height || 550,
                    "colorTheme": config.colorTheme || "light",
                    "isTransparent": config.isTransparent || false,
                    "locale": config.locale || "en",
                    "importanceFilter": config.importanceFilter || "-1,0,1",
                    "countryFilter": config.countryFilter || "ar,au,br,ca,cn,fr,de,in,id,it,jp,kr,mx,ru,sa,za,tr,gb,us,eu"
                } );
                
                script.onload = () => setIsLoading(false);
                script.onerror = () => { setError('Failed to load calendar widget'); setIsLoading(false); };
                
                container.appendChild(script);
            } catch (e) {
                container.innerHTML = '<p>Error loading economic calendar</p>';
                setError('Error creating calendar widget');
                setIsLoading(false);
            }
        } else {
            setTimeout(initWidget, 100);
        }
    };
    
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWidget);
    else setTimeout(initWidget, 100);
}

// Allow string or BackendResponse or undefined
interface SmartRendererProps {
    content: string | BackendResponse | undefined;
}

const STOCKBOT_CURSOR_RE = /\[(?:\u200B)?\]\(#cursor\)/g;

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const SmartRenderer: React.FC<SmartRendererProps> = ({ content }) => {
    const isStr = typeof content === 'string';
    const rawString = isStr ? (content as string) : '';
    const cleanedString = isStr ? rawString.replace(STOCKBOT_CURSOR_RE, '') : '';

    const previewSource = isStr ? cleanedString : JSON.stringify(content || '');
    console.log('🔍 Smart renderer analyzing content:', (previewSource || '').slice(0, 100) + '...');

    // If this is only the streaming cursor placeholder, render the cursor UI instead
    // of sending raw markdown to the legacy HTML executor.
    if (isStr && rawString.trim().replace(STOCKBOT_CURSOR_RE, '').trim() === '') {
        return <span className="cursor-blink" />;
    }

    let parsed: BackendResponse | null = null;

    if (!isStr && content && typeof content === 'object') {
        parsed = content as BackendResponse;
    } else if (isStr) {
        try {
            parsed = JSON.parse(cleanedString);
        } catch {
            parsed = null;
        }
    } else {
        parsed = null;
    }

    if (parsed && parsed.success && parsed.widget) {
        console.log('📦 Parsed backend response:', parsed);
        console.log('✅ Detected JSON response with widget');
        return (
            <div>
                {parsed.response_text && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: `<p>${escapeHtml(parsed.response_text)}</p>`
                        }}
                    />
                )}
                <div className="widget-container stockbot-widget-container">
                    <h3>
                        {_getWidgetDisplayName(parsed.widget.type)}{' '}
                        {parsed.widget.symbol ? `- ${parsed.widget.symbol}` : ''}
                    </h3>
                    <TradingViewWidgetRenderer widget={parsed.widget} />
                </div>
            </div>
        );
    }

    if (parsed && parsed.success) {
        console.log('📦 Parsed backend response:', parsed);
        console.log('✅ Detected JSON response without widget');
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: `<p>${escapeHtml(parsed.response_text || '')}</p>`
                }}
            />
        );
    }

    if (parsed && parsed.success === false) {
        console.log('📦 Parsed backend response:', parsed);
        console.log('❌ JSON response indicates error');
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: `<p>Error: ${escapeHtml(parsed.error || 'Unknown error')}</p>`
                }}
            />
        );
    }

    console.log('📄 Detected legacy HTML format, using script execution');
    return <ScriptExecutingHtml html={cleanedString} />;
};

// Main component interface
export interface StockbotMessageProps {
    messageHtml: string | BackendResponse | undefined;
    timeStamp: string
    isBotResponse: boolean
    // NEW: Add optional attachment property
    attachment?: {
        fileName: string
        fileSize: number
        fileType: string
        uploadID?: number
    }
}

export function StockbotMessage({messageHtml, timeStamp, isBotResponse, attachment}: StockbotMessageProps): ReactElement {
    const messageClasses = clsx(!isBotResponse && "justify-content-end", "d-flex align-items-baseline mb-2")
    
    const isString = typeof messageHtml === 'string';
    const contentLength = isString
        ? (messageHtml as string).length
        : JSON.stringify(messageHtml || {}).length;

    const stockbotWidget = (() => {
        if (!isBotResponse || !messageHtml) return null;

        if (typeof messageHtml === 'object' && 'widget' in messageHtml) {
            return (messageHtml as BackendResponse).widget;
        }

        if (typeof messageHtml === 'string') {
            try {
                const parsed = JSON.parse(messageHtml) as BackendResponse;
                return parsed?.widget || null;
            } catch {
                return null;
            }
        }

        return null;
    })();

    const isWideStockbotChartWidget =
        !!stockbotWidget &&
        ['chart', 'technical_analysis'].includes(stockbotWidget.type);


    console.log('💬 StockbotMessage rendering:', {
        isBotResponse,
        hasContent: contentLength > 0,
        contentLength,
        timeStamp,
        approach: 'JSON-BASED WITH FLASK APP WIDGET LOGIC'
    });
    
    return (
        <div className={messageClasses}>
            <div
                style={
                    isWideStockbotChartWidget
                        ? { width: 'min(860px, calc(100vw - 48px))', maxWidth: 'min(860px, calc(100vw - 48px))' }
                        : { maxWidth: '85%' }
                }
            >
                <div
                    className={clsx(
                        isBotResponse && "bg-secondary",
                        !isBotResponse && "enlive-gradient",
                        "card display-inline-block p-3 my-2 bg-inherit",
                        isWideStockbotChartWidget && "stockbot-wide-chart-card"
                    )}
                >
                    <SmartRenderer content={messageHtml} />
                    
                    {/* NEW: Render attachment card if present */}
                    {attachment && (
                        <MessageAttachmentCard
                            fileName={attachment.fileName}
                            fileSize={attachment.fileSize}
                            fileType={attachment.fileType}
                            uploadID={attachment.uploadID}
                        />
                    )}
                </div>
                <div className={clsx(!isBotResponse && "text-end", "small px-2")}>
                    {timeStamp}
                </div>
            </div>
        </div>
    );
}